<script setup>
import { ref, computed, watch } from 'vue'
import { useNotification } from '@kyvg/vue3-notification'
import AudienceService from '@/services/AudienceService'
import Spinner from '@/components/ui/Spinner.vue'
import Alert from '@/components/Atoms/Alert.vue'
import FormButton from '@/components/Atoms/FormButton.vue'
import ModalBox from '@/components/modals/ModalBox.vue'
import { MOBILE_SHEET_WIDTH } from './audienceDialog'

// Bans in force for a room, with the undo the rest of the tab has no way to
// offer: stats only ever gave a count, so a ban placed in the heat of a stream
// could never be found again, let alone lifted.
//
// A dialog rather than a pane in the room card. Bans are consulted when
// something needs undoing, not watched, and the room card's vertical space is
// already spoken for by the message list a moderator reads continuously.

const { notify } = useNotification()

const props = defineProps({
	streamId: {
		type: String,
		required: true
	}
})

const emit = defineEmits(['changed'])

const isVisible = defineModel({ type: Boolean, default: false })

const bans = ref([])
const loading = ref(false)
const loadError = ref('')
const busyVid = ref('')

// Refetched on every open rather than cached: a ban can be placed from the chat
// pane while this is closed, and a timed one can lapse between openings.
watch(isVisible, (open) => {
	if (open) load()
})

const load = async () => {
	loading.value = true
	loadError.value = ''

	try {
		const res = await AudienceService.listBans(props.streamId)
		bans.value = (res && res.bans) || []
	} catch (err) {
		loadError.value = err.message || 'Could not load the ban list'
	}

	loading.value = false
}

/**
 * Viewer ids are opaque — `anon-8f2c1d` — and a column of them is unreadable.
 * The shared prefix is dimmed so the eye lands on the part that differs.
 */
const splitVid = (vid) => {
	const at = String(vid).lastIndexOf('-')
	if (at <= 0) return { prefix: '', suffix: String(vid) }
	return { prefix: String(vid).slice(0, at + 1), suffix: String(vid).slice(at + 1) }
}

/** A stable colour per id, so the same viewer looks the same on every visit. */
const hueFor = (vid) => {
	let hash = 0
	for (let i = 0; i < vid.length; i += 1) {
		hash = (hash * 31 + vid.charCodeAt(i)) % 360
	}
	return hash
}

const describeUntil = (until) => {
	if (!until) return 'Permanent'

	const ms = until - Date.now()
	if (ms <= 0) return 'Expiring now'

	const minutes = Math.round(ms / 60000)
	if (minutes < 60) return `Expires in ${minutes} minute${minutes === 1 ? '' : 's'}`

	const hours = Math.round(minutes / 60)
	if (hours < 48) return `Expires in ${hours} hour${hours === 1 ? '' : 's'}`

	return `Expires in ${Math.round(hours / 24)} days`
}

const exactUntil = (until) => (until ? new Date(until).toLocaleString() : 'Never expires')

const permanentCount = computed(() => bans.value.filter((ban) => !ban.until).length)

const unban = async (ban) => {
	busyVid.value = ban.vid

	try {
		await AudienceService.unbanViewer(props.streamId, ban.vid)
		bans.value = bans.value.filter((item) => item.vid !== ban.vid)
		notify({ group: 'success', text: 'Ban lifted' })
		// The room card shows a ban count from stats, which this has just
		// invalidated.
		emit('changed')
	} catch (err) {
		notify({ group: 'error', text: err.message || 'Could not lift the ban' })
	}

	busyVid.value = ''
}
</script>

<template>
	<ModalBox v-model="isVisible" title="Banned viewers" size="md" scroll-body
		:wrapper-classes="MOBILE_SHEET_WIDTH">
		<div class="px-4 pb-4 sm:px-6 sm:pb-6">
			<div v-if="loading" class="py-10">
				<Spinner />
			</div>

			<template v-else-if="loadError">
				<Alert type="error" :text="loadError" />
				<div class="flex justify-center mt-3">
					<FormButton type="secondary" size="sm" @click="load">Try again</FormButton>
				</div>
			</template>

			<div v-else-if="!bans.length" class="text-center py-8">
				<p class="text-sm text-surface-text-1 mb-1">Nobody is banned.</p>
				<p class="text-tiny text-surface-7 mb-0">
					Ban a viewer from the chat pane and they will show up here, where you can lift it again.
				</p>
			</div>

			<template v-else>
				<p class="text-tiny text-surface-7 mb-3">
					{{ bans.length }} viewer{{ bans.length === 1 ? '' : 's' }} banned,
					{{ permanentCount }} of them permanently. Newest first. Timed bans disappear from this
					list on their own once they lapse.
				</p>

				<ul class="list-none p-0 m-0 divide-y divide-surface-3">
					<li v-for="ban in bans" :key="ban.vid"
						class="flex items-center justify-between gap-x-3 py-2.5">
						<div class="flex items-center gap-x-3 min-w-0">
							<span class="shrink-0 w-2.5 h-2.5 rounded-full"
								:style="{ backgroundColor: `hsl(${hueFor(ban.vid)}, 62%, 58%)` }"></span>
							<span class="font-mono text-sm truncate" :title="ban.vid">
								<span class="text-surface-7">{{ splitVid(ban.vid).prefix }}</span><span
									class="text-surface-text-1">{{ splitVid(ban.vid).suffix }}</span>
							</span>
							<span class="shrink-0 text-tiny rounded px-1.5 py-0.5"
								:class="ban.until ? 'bg-surface-3 text-surface-8' : 'bg-error/20 text-error'"
								:title="exactUntil(ban.until)">
								{{ describeUntil(ban.until) }}
							</span>
						</div>

						<FormButton type="secondary" size="sm" :disabled="busyVid === ban.vid"
							:is-loading="busyVid === ban.vid" @click="unban(ban)">
							Unban
						</FormButton>
					</li>
				</ul>
			</template>
		</div>

		<template #footer>
			<footer class="flex justify-between gap-2 border-t border-surface-3 p-4 sm:p-6">
				<FormButton type="link" size="sm" :disabled="loading" @click="load">Refresh</FormButton>
				<FormButton type="secondary" @click="isVisible = false">Close</FormButton>
			</footer>
		</template>
	</ModalBox>
</template>
